/**
 * Smart image selection utility for vehicle listings
 * Detects and avoids interior/dashboard/detail shots, preferring full exterior views
 */

// Patterns that indicate interior/dashboard images (should be avoided for card thumbnails)
const INTERIOR_PATTERNS = [
  // Dashboard/speedometer indicators
  'dashboard', 'bord', 'cockpit', 'interior', 'cabin',
  'speedometer', 'odometer', 'km/h', 'mph',
  'steering', 'volan', 'wheel',
  'gear', 'schimbator', 'shifter', 'lever',
  'seat', 'scaun', 'seats',
  'console', 'consola',
  'display', 'screen', 'ecran',
  'radio', 'navigation', 'navi', 'gps',
  // Common interior image naming patterns
  '_int', '-int', 'int_', 'int-',
  '_in_', '-in-', '_in.', '-in.',
  'inside', 'innen',
];

// Patterns that indicate detail/close-up shots (should be avoided)
const DETAIL_PATTERNS = [
  // Close-up parts
  'headlight', 'far', 'light', 'lamp', 'lumini',
  'taillight', 'stop', 'rear-light',
  'wheel', 'rim', 'janta', 'tire', 'tyre', 'roata',
  'mirror', 'oglinda',
  'badge', 'emblem', 'logo',
  'engine', 'motor', 'capota',
  'trunk', 'portbagaj', 'boot',
  'door', 'usa', 'handle', 'maner',
  'exhaust', 'evacuare', 'teava',
  'bumper', 'bara',
  // Common detail image naming patterns
  '_det', '-det', 'det_', 'det-',
  '_detail', '-detail',
  'close', 'closeup', 'close-up', 'macro',
  'zoom', '_z_',
];

// Patterns that indicate partial/cropped views (less desirable)
const PARTIAL_PATTERNS = [
  'roof', 'acoperis', 'plafon',
  'side', 'lateral',
  'corner', 'colt',
  'partial', 'crop',
  '_part', '-part',
];

// Patterns that strongly indicate full exterior shots (highly preferred)
const EXTERIOR_PATTERNS = [
  // Common exterior naming conventions
  'front', 'fata', 'frontal',
  'exterior', 'ext', 'outside',
  'full', 'complet', 'complete',
  '3/4', '34', 'three-quarter',
  'hero', 'main', 'principal', 'cover',
  '_01', '_1.', '-01', '-1.',
  '_02', '_2.', '-02', '-2.',
  'thumb', 'thumbnail', 'preview',
];

// Demo/placeholder patterns (always avoid)
const DEMO_PATTERNS = [
  '/demo-images/',
  'demo-', '-demo',
  'placeholder',
  'no-image',
  'default-car',
  'coming-soon',
  'no-photo',
];

/**
 * Calculate a score for an image URL based on how likely it is to be a good exterior shot
 * Higher score = better image for card thumbnail
 * @param url - Image URL to score
 * @returns Score from -100 to +100
 */
/**
 * Detect filenames that carry no semantic information (UUIDs, hashes, random IDs).
 * Uploaded photos are stored as UUIDs, so keyword matching on them produces
 * false signals (e.g. the hex sequence "34" being read as a "three-quarter" shot).
 */
function hasOpaqueFilename(url: string): boolean {
  const filename = (url.toLowerCase().split('?')[0].split('/').pop() || '')
    .replace(/\.(jpe?g|png|webp|avif|gif)$/i, '');
  // UUID (with or without dashes) or long hex/base-ish hash
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(filename)) return true;
  if (/^[0-9a-f]{16,}$/.test(filename)) return true;
  // No alphabetic token longer than 3 chars that isn't pure hex → no usable words
  const words = filename.split(/[^a-z]+/).filter(w => w.length >= 4 && !/^[a-f]+$/.test(w));
  return words.length === 0;
}

export function scoreImageUrl(url: string | undefined | null): number {
  if (!url) return -100;
  
  const normalized = url.toLowerCase();
  let score = 0;

  
  // Check for demo/placeholder images (instant disqualification)
  for (const pattern of DEMO_PATTERNS) {
    if (normalized.includes(pattern)) {
      return -100;
    }
  }

  // Opaque filenames (UUIDs/hashes from uploads) carry no signal — stay neutral
  // so the seller's own photo order decides the cover image.
  if (hasOpaqueFilename(normalized)) {
    return 0;
  }
  

  
  // Penalize interior shots heavily
  for (const pattern of INTERIOR_PATTERNS) {
    if (normalized.includes(pattern)) {
      score -= 50;
    }
  }
  
  // Penalize detail/close-up shots
  for (const pattern of DETAIL_PATTERNS) {
    if (normalized.includes(pattern)) {
      score -= 30;
    }
  }
  
  // Slightly penalize partial/cropped views
  for (const pattern of PARTIAL_PATTERNS) {
    if (normalized.includes(pattern)) {
      score -= 15;
    }
  }
  
  // Reward exterior shot indicators
  for (const pattern of EXTERIOR_PATTERNS) {
    if (normalized.includes(pattern)) {
      score += 25;
    }
  }
  
  // IMPORTANT: Detect image sequence numbers like _01, _02, -01, -02
  // Only match numbers that appear near the END of the filename (right before the
  // extension or a hash suffix). This avoids matching years (e.g. -2011-, -2012-)
  // or unrelated numbers in the middle of slugs (e.g. -117-cp-, -174-000-km-).
  // Examples that match: "...-5.jpg", "...-18-abcdef.jpg", "..._02_scaled.webp"
  const filename = normalized.split('/').pop() || normalized;
  const sequenceMatch = filename.match(/[_-](\d{1,3})(?:[_-][a-z0-9]{6,})?(?:[._-](?:scaled|large|medium|thumb))?\.(?:jpe?g|png|webp|avif)(?:[?#]|$)/i);
  if (sequenceMatch) {
    const num = parseInt(sequenceMatch[1], 10);
    // Skip 4-digit years (1900-2099) — they aren't photo sequence numbers
    if (num >= 1900 && num <= 2099) {
      // ignore
    } else if (num <= 4) {
      // First 4 images get strong bonus (01=+40, 02=+30, 03=+20, 04=+10)
      score += (5 - num) * 10;
    } else if (num >= 12) {
      // High numbered images are almost certainly interior
      score -= 40;
    } else if (num >= 8) {
      // Images 08+ are likely interior/dashboard shots - penalize
      score -= 20;
    }
  }

  // Bonus: WordPress/Elementor "featured image" lives at /uploads/YYYY/MM/slug.jpg
  // and is almost always the chosen cover (exterior). Derivative gallery thumbs
  // live under /elementor/thumbs/ and are typically secondary/interior shots.
  if (/\/uploads\/\d{4}\/\d{2}\//.test(normalized) && !normalized.includes('/elementor/')) {
    score += 60;
  }
  if (normalized.includes('/elementor/thumbs/') || normalized.includes('/thumbs/')) {
    score -= 20;
  }
  
  return Math.max(-100, Math.min(100, score));
}

/**
 * Check if an image URL appears to be a demo or placeholder image
 */
export function isDemoImage(url: string | undefined | null): boolean {
  if (!url) return true;
  const normalized = url.toLowerCase();
  return DEMO_PATTERNS.some(pattern => normalized.includes(pattern));
}

/**
 * Check if an image URL appears to be an interior/dashboard shot
 */
export function isInteriorImage(url: string | undefined | null): boolean {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return INTERIOR_PATTERNS.some(pattern => normalized.includes(pattern));
}

/**
 * Check if an image URL appears to be a detail/close-up shot
 */
export function isDetailImage(url: string | undefined | null): boolean {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return DETAIL_PATTERNS.some(pattern => normalized.includes(pattern));
}

/**
 * Select the best image from an array of URLs for use as a card thumbnail
 * Prioritizes full exterior shots and avoids interior/detail images
 * @param images - Array of image URLs
 * @param fallbackPhoto - Single photo URL as fallback
 * @returns Best image URL or null if none suitable
 */
export function selectBestCardImage(
  images: string[] | undefined | null, 
  fallbackPhoto?: string | null
): string | null {
  // If no images array, try fallback
  if (!images || images.length === 0) {
    if (fallbackPhoto && !isDemoImage(fallbackPhoto)) {
      return fallbackPhoto;
    }
    return null;
  }
  
  // Filter valid images
  const validImages = images.filter(url => url && !isDemoImage(url));
  
  if (validImages.length === 0) {
    if (fallbackPhoto && !isDemoImage(fallbackPhoto)) {
      return fallbackPhoto;
    }
    return null;
  }
  
  // SMART STRATEGY: Always prefer exterior shots from first 4 images
  // Autovit and most dealers order photos: exterior first, interior later
  
  // Only analyze first 4 images (these are typically exterior shots)
  const candidateImages = validImages.slice(0, Math.min(4, validImages.length));
  
  // Score candidates to avoid any interior shots that slipped in
  const scoredImages = candidateImages.map((url, index) => ({
    url,
    score: scoreImageUrl(url),
    originalIndex: index
  }));
  
  // Sort by score (highest first)
  scoredImages.sort((a, b) => b.score - a.score);
  
  // Find the best non-negative scored image
  const bestPositive = scoredImages.find(img => img.score >= 0);
  if (bestPositive) {
    return bestPositive.url;
  }
  
  // If all candidates have negative scores (interior detected), 
  // still prefer the least negative one from candidates
  if (scoredImages.length > 0 && scoredImages[0].score > -50) {
    return scoredImages[0].url;
  }
  
  // Ultimate fallback: first valid image
  return validImages[0];
}

/**
 * Check if an image URL has valid photo content (not demo/placeholder)
 */
export function hasValidPhoto(photo: string | undefined | null): boolean {
  return !!photo && !isDemoImage(photo);
}
